import React, { useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Plus, Edit3, Eye, RefreshCw, Send, Trash2 } from 'lucide-react';
import { useNotification } from '@kardume/hooks';
import { backofficeApi, extractErrorMessage, formatNumber } from '@kardume/services';
import type {
  BackofficeEmployeeDto,
  BackofficeEmployeeRequestDto,
  BackofficeAccessProfileDto,
  AccountStatus,
  Tone,
  ComboItem,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  Avatar,
  Switch,
  SearchInput,
  Modal,
  ConfirmModal,
  TextInput,
  PhoneInput,
  DocumentInput,
  SearchableComboBox,
  formatCpf,
  formatPhone,
  Select,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import EmployeeDetailDrawer from '../components/EmployeeDetailDrawer';
import EmailChannelPanel from '../components/EmailChannelPanel';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Person = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .email {
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
  }
  .id {
    font-family: monospace;
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
  }
`;

const DocCell = styled.div`
  .k {
    font-size: 0.7rem;
    color: ${(p) => p.theme.textMuted};
  }
  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

const ROLE_OPTIONS = [
  { value: 'BACKOFFICE', label: 'Backoffice' },
  { value: 'ADMIN', label: 'Administrador' },
];

const ROLE_FILTER = [{ value: '', label: 'Todos os cargos' }, ...ROLE_OPTIONS];

const STATUS_LABELS: Record<AccountStatus, string> = {
  ACTIVE: 'Ativo',
  PENDING_ACTIVATION: 'Pendente',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'INACTIVE', label: 'Inativo' },
  { value: 'BLOCKED', label: 'Bloqueado' },
];

function statusTone(status: AccountStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_ACTIVATION') return 'warning';
  if (status === 'BLOCKED') return 'danger';
  return 'secondary';
}

const roleTone = (role: BackofficeEmployeeDto['role']): Tone => (role === 'ADMIN' ? 'danger' : 'info');

/**
 * Estado do convite de acesso. Um funcionário cujo e-mail de credenciais falhou
 * existe na lista, parece normal - e não consegue entrar, porque ninguém sabe a
 * senha temporária dele. Esta coluna é o que separa os dois casos.
 */
function credentialsState(e: BackofficeEmployeeDto): { label: string; tone: Tone; title?: string } {
  if (e.credentialsSentAt) {
    return { label: 'Enviadas', tone: 'success', title: new Date(e.credentialsSentAt).toLocaleString('pt-BR') };
  }
  if (e.credentialsError) return { label: 'Falhou', tone: 'danger', title: e.credentialsError };
  return { label: 'Não enviadas', tone: 'warning' };
}
const isAccessAllowed = (e: BackofficeEmployeeDto): boolean => e.accessEnabled ?? e.status === 'ACTIVE';

interface EmployeeForm extends BackofficeEmployeeRequestDto {
  active: boolean;
  accessEnabled: boolean;
  accessProfileId: string;
}

const EMPTY_FORM: EmployeeForm = {
  firstName: '',
  lastName: '',
  email: '',
  document: '',
  phone: '',
  role: 'BACKOFFICE',
  active: true,
  accessEnabled: true,
  accessProfileId: '',
};

const EmployeesPage: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.listEmployees({ size: 100 }), []);
  const profilesState = useAsync(() => backofficeApi.listAccessProfiles(), []);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EmployeeForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<BackofficeEmployeeDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState<BackofficeEmployeeDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BackofficeEmployeeDto | null>(null);
  const [actionPending, setActionPending] = useState(false);

  const employees = useMemo<BackofficeEmployeeDto[]>(() => data?.content ?? [], [data]);
  const profiles = useMemo<BackofficeAccessProfileDto[]>(() => profilesState.data ?? [], [profilesState.data]);

  const profileItems = useMemo<ComboItem[]>(
    () => [{ id: '', label: 'Sem perfil' }, ...profiles.map((p) => ({ id: p.id, label: p.name }))],
    [profiles]
  );

  const stats = useMemo(
    () => ({
      total: data?.totalElements ?? employees.length,
      active: employees.filter((e) => e.status === 'ACTIVE').length,
      access: employees.filter(isAccessAllowed).length,
      admins: employees.filter((e) => e.role === 'ADMIN').length,
    }),
    [data, employees]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees.filter((e) => {
      const matchesRole = !roleFilter || e.role === roleFilter;
      const matchesStatus = !statusFilter || e.status === statusFilter;
      const matchesTerm =
        !term ||
        [`${e.firstName} ${e.lastName}`, e.email, e.document ?? ''].some((v) => v.toLowerCase().includes(term));
      return matchesRole && matchesStatus && matchesTerm;
    });
  }, [employees, search, roleFilter, statusFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const openEdit = (employee: BackofficeEmployeeDto) => {
    setEditingId(employee.id);
    setForm({
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      document: employee.document ?? '',
      phone: employee.phone ?? '',
      role: employee.role,
      active: employee.status === 'ACTIVE',
      accessEnabled: isAccessAllowed(employee),
      accessProfileId: employee.accessProfileId ?? '',
    });
    setIsModalOpen(true);
  };

  const openDetail = (employee: BackofficeEmployeeDto) => {
    setDetail(employee);
    setDetailOpen(true);
  };

  const setField = <K extends keyof EmployeeForm>(key: K, value: EmployeeForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  /**
   * Reenvia as credenciais com uma nova senha temporária - também é o "forçar
   * troca de senha": a senha atual deixa de valer. Por isso a confirmação, e por
   * isso o erro do backend é mostrado inteiro: quando o e-mail não sai, nada é
   * alterado, e o admin precisa saber o motivo para poder corrigir.
   */
  const confirmResend = async () => {
    if (!resendTarget || actionPending) return;
    setActionPending(true);
    try {
      await backofficeApi.resendEmployeeCredentials(resendTarget.id);
      showNotification(`Credenciais enviadas para ${resendTarget.email}.`, 'success');
      setResendTarget(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível reenviar as credenciais.'), 'error');
    } finally {
      setActionPending(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || actionPending) return;
    setActionPending(true);
    try {
      await backofficeApi.deleteEmployee(deleteTarget.id);
      showNotification('Funcionário excluído.', 'success');
      setDeleteTarget(null);
      reload();
    } catch (err) {
      // O backend recusa excluir o último administrador; a mensagem dele explica.
      showNotification(extractErrorMessage(err, 'Não foi possível excluir o funcionário.'), 'error');
    } finally {
      setActionPending(false);
    }
  };

  const handleSubmit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload: BackofficeEmployeeRequestDto = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        document: form.document,
        phone: form.phone,
        role: form.role,
        active: form.active,
        accessEnabled: form.accessEnabled,
        accessProfileId: form.accessProfileId ? form.accessProfileId : null,
      };
      if (editingId) {
        await backofficeApi.updateEmployee(editingId, payload);
        showNotification('Funcionário atualizado com sucesso.', 'success');
      } else {
        await backofficeApi.createEmployee(payload);
        showNotification('Funcionário criado com sucesso.', 'success');
      }
      setIsModalOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o funcionário.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Funcionários"
        subtitle="Gestão de usuários do backoffice e seus perfis de acesso."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Novo funcionário
          </Button>
        }
      />

      {/*
        O diagnóstico do e-mail fica aqui, e não numa tela de configurações: é
        nesta página que o sintoma aparece (o convite não chegou), e é aqui que
        alguém vai procurar a explicação.
      */}
      <EmailChannelPanel />

      <StatsGrid>
        <StatCard label="Total de usuários" value={formatNumber(stats.total)} />
        <StatCard label="Ativos" value={formatNumber(stats.active)} />
        <StatCard label="Acesso liberado" value={formatNumber(stats.access)} />
        <StatCard label="Administradores" value={formatNumber(stats.admins)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome, e-mail ou CPF..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select options={ROLE_FILTER} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} />
        <Select options={STATUS_FILTER} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={filtered.length === 0}
        emptyTitle="Nenhum funcionário encontrado"
        emptyDescription="Cadastre membros da equipe para dar acesso ao backoffice."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Funcionário</th>
                <th>CPF</th>
                <th style={{ textAlign: 'right' }}>Cargo</th>
                <th style={{ textAlign: 'right' }}>Status</th>
                <th style={{ textAlign: 'right' }}>Acesso</th>
                <th style={{ textAlign: 'right' }}>Perfil de acesso</th>
                <th style={{ textAlign: 'right' }}>Credenciais</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const allowed = isAccessAllowed(e);
                return (
                  <tr key={e.id}>
                    <td>
                      <Person>
                        <Avatar name={`${e.firstName} ${e.lastName}`} />
                        <div>
                          <strong>
                            {e.firstName} {e.lastName}
                          </strong>
                          <div className="email">{e.email}</div>
                          <div className="id">ID: {e.id.slice(0, 8)}...</div>
                        </div>
                      </Person>
                    </td>
                    <td>
                      <DocCell>
                        <div className="k">CPF</div>
                        <strong>{e.document ? formatCpf(e.document) : '-'}</strong>
                        {e.phone && <span>{formatPhone(e.phone)}</span>}
                      </DocCell>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={roleTone(e.role)}>{e.role}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={statusTone(e.status)}>{STATUS_LABELS[e.status]}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={allowed ? 'success' : 'danger'}>{allowed ? 'Liberado' : 'Bloqueado'}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {e.role === 'ADMIN' ? (
                        <Badge tone="danger">Acesso total</Badge>
                      ) : e.accessProfileName ? (
                        <Badge tone="info">{e.accessProfileName}</Badge>
                      ) : (
                        <Badge tone="secondary">Sem perfil</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {(() => {
                        const c = credentialsState(e);
                        // `title` no span, e nao no Badge: o Badge nao repassa
                        // atributos de HTML, e a causa da falha precisa ficar
                        // legivel no hover sem ocupar uma coluna inteira.
                        return (
                          <span title={c.title}>
                            <Badge tone={c.tone}>{c.label}</Badge>
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <RowActions>
                        <ActionButton title="Ver detalhes" onClick={() => openDetail(e)}>
                          <Eye size={16} />
                        </ActionButton>
                        <ActionButton title="Editar" onClick={() => openEdit(e)}>
                          <Edit3 size={16} />
                        </ActionButton>
                        <ActionButton
                          title="Reenviar credenciais (nova senha temporária)"
                          onClick={() => setResendTarget(e)}
                        >
                          <Send size={16} />
                        </ActionButton>
                        <ActionButton title="Excluir" color={theme.error} onClick={() => setDeleteTarget(e)}>
                          <Trash2 size={16} />
                        </ActionButton>
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <EmployeeDetailDrawer
        employee={detail}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onEdit={(e) => {
          setDetailOpen(false);
          openEdit(e);
        }}
      />

      <ConfirmModal
        isOpen={!!resendTarget}
        title="Reenviar credenciais"
        message={
          <>
            Enviar novas credenciais para <strong>{resendTarget?.email}</strong>? A senha atual deixa
            de funcionar e a troca será exigida no próximo acesso.
          </>
        }
        confirmLabel="Enviar"
        loading={actionPending}
        onConfirm={confirmResend}
        onClose={() => setResendTarget(null)}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        danger
        title="Excluir funcionário"
        message={
          <>
            Excluir <strong>
              {deleteTarget?.firstName} {deleteTarget?.lastName}
            </strong>? O acesso é revogado imediatamente, inclusive nas sessões abertas.
          </>
        }
        confirmLabel="Excluir"
        loading={actionPending}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? 'Editar funcionário' : 'Novo funcionário'}
        width="640px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSubmit} isLoading={saving}>
              {editingId ? 'Salvar alterações' : 'Criar'}
            </Button>
          </>
        }
      >
        <Form>
          <Row>
            <TextInput label="Nome" value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} />
            <TextInput label="Sobrenome" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} />
          </Row>
          <Row>
            <TextInput label="E-mail" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
            <PhoneInput label="Telefone" value={form.phone ?? ''} onChange={(v) => setField('phone', v)} />
          </Row>
          <Row>
            <DocumentInput label="CPF" documentType="CPF" placeholder="000.000.000-00" value={form.document ?? ''} onChange={(v) => setField('document', v)} />
            <Select
              label="Cargo"
              options={ROLE_OPTIONS}
              value={form.role}
              onChange={(e) => setField('role', e.target.value as EmployeeForm['role'])}
            />
          </Row>
          <SearchableComboBox
            label="Perfil de acesso"
            placeholder="Selecione um perfil"
            items={profileItems}
            valueId={form.accessProfileId}
            onChange={(id) => setField('accessProfileId', id)}
          />
          {form.role === 'ADMIN' ? (
            <Hint>Administradores têm acesso total ao backoffice - o perfil de acesso é opcional.</Hint>
          ) : (
            <Hint>As permissões do funcionário são definidas pelo perfil de acesso selecionado.</Hint>
          )}
          <Row>
            <ToggleField>
              <Switch checked={form.active} onChange={(v) => setField('active', v)} />
              Status ativo
            </ToggleField>
            <ToggleField>
              <Switch checked={form.accessEnabled} onChange={(v) => setField('accessEnabled', v)} />
              Acesso ao sistema ativo
            </ToggleField>
          </Row>
          <Hint>Quando o acesso é desativado, o funcionário permanece cadastrado mas não consegue acessar o backoffice.</Hint>
        </Form>
      </Modal>
    </>
  );
};

export default EmployeesPage;
